import type { AxiosResponse } from "axios";
import axios from "@/services/middleware/client";
import type {
  PasswordChangePayload,
  AccountSettings,
  MeUser,
  AccountUser,
  UserAPIKeys,
} from "@/types/model/User";
import { useLoaderStore } from "@/zustand/loaderStore";

export function getUser(): Promise<AxiosResponse<MeUser> | void> {
  return axios
    .get("/me")
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function getAccountUsers(): Promise<AxiosResponse<AccountUser[]>> {
  return axios.get("/account/users").then((res) => res);
}

export function inviteUser(payload: {
  email: string;
  roles: {
    [k: string]: {
      labels: Array<number>;
      trafficSources: Array<number>;
    };
  };
  // Multidomain: restricts the invited user to a single sensor (child account).
  sensorId?: number;
}): Promise<AxiosResponse> {
  return axios.post("/account/invite", payload).then((res) => res);
}

export function revokeAllUserRoles(
  id: number,
  userRole: { [role: string]: Record<string, never> }
): Promise<AxiosResponse> {
  return axios
    .delete(`/account/users/${id}`, { data: userRole })
    .then((res) => res);
}

export function grantUserRoles(
  id: number,
  payload: Record<string, unknown>
): Promise<AxiosResponse> {
  return axios.put(`/account/users/${id}`, payload).then((res) => res);
}

export function getUserSettings(): Promise<AxiosResponse<AccountSettings>> {
  return axios.get("/me/settings").then((res) => res);
}

export function getWhitelistedAddresses(): Promise<
  AxiosResponse<Array<string>>
> {
  return axios.get("/me/ip-whitelist").then((res) => res);
}

export function updateUserSettings(
  settings: AccountSettings
): Promise<AxiosResponse | void> {
  return axios
    .put("/me/settings", settings)
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    })
    .finally(() => {
      useLoaderStore.getState().setLoading(false);
    });
}

export function updateIPWhitelist(
  ipAddresses: string[]
): Promise<AxiosResponse | void> {
  return axios
    .post("/me/ip-whitelist", ipAddresses)
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function updateIPBlacklist(
  ipAddresses: string[]
): Promise<AxiosResponse | void> {
  return axios
    .post("/me/ip-blacklist", ipAddresses)
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function getUserAPIKeys(): Promise<AxiosResponse<UserAPIKeys> | void> {
  return axios
    .get("/me/keys")
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function createAPIKey(name: {
  name: string;
}): Promise<AxiosResponse | void> {
  return axios
    .post("/me/keys", name)
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function revokeAPIKey(key: string): Promise<AxiosResponse | void> {
  return axios
    .delete("/me/keys", { data: { key } })
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function verifyUserEmail(
  email: string | null,
  hash: string | null
): Promise<void> {
  return axios
    .post("/auth/email/verify", {
      email,
      hash,
    })
    .then((res) => res.data)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function updateSecuritySettings(
  securitySettings: PasswordChangePayload
): Promise<AxiosResponse | void> {
  return axios
    .post("/auth/password/change", securitySettings)
    .then((res) => res)
    .catch((error) => {
      console.error("axios error", error);
    });
}

export function updateOnboarded(
  payload: Record<string, boolean>
): Promise<void> {
  return axios
    .put("/me/onboarded", payload)
    .then((res) => res.data)
    .catch((error) => {
      console.error("axios error", error);
    });
}
